import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import {
  adminDeleteCourseTemplate,
  adminUpdateCourseTemplate,
  createCourseTemplateAsAdmin,
  getCourseTemplateByIdAsAdmin,
  getCourseTemplatesAsAdmin,
  loadMoreCourseTemplatesAsAdmin,
} from './adminApi';
import { RootState } from '../..';
import { ISectionTemplate } from '../SectionTemplateSlice';
import { IUser } from '../../UserSlice';
import { ICourse } from '../CoursesSlice';

export interface ICourseTemplate {
  _id: string;
  name: string;
  title: string;
  course: string | ICourse;
  classDays?: string[];
  classTime?: string;
  startDate?: string;
  endDate?: string;
  currency?: string;
  amount?: string;
  sections?: ISectionTemplate[];
  createdAt: string;
  createdBy: string | IUser;
  lastUpdatedBy: string | IUser;
  curriculum?: string;
  subject?: string;
  grade?: string;
  isTrialTemplate?: boolean;
}

export interface IInitialCourseTemplatesState {
  status: 'idle' | 'loading' | 'rejected';
  adminCourseTemplatesData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourseTemplate>;
  };
  adminCourseTemplate?: ICourseTemplate;
}

const initialState: IInitialCourseTemplatesState = {
  status: 'idle',
  // Admin
  adminCourseTemplatesData: undefined,
  adminCourseTemplate: undefined,
};

export const CourseTemplatesState = createSlice({
  name: 'courseTemplates',
  initialState,
  reducers: {
    resetAdminCourseTemplatesData: (state) => {
      state.adminCourseTemplatesData = undefined;
    },
    resetAdminCourseTemplate: (state) => {
      state.adminCourseTemplate = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCourseTemplateAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getCourseTemplatesAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newAdminCourseTemplatesData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCourseTemplatesData = newAdminCourseTemplatesData;
      })
      .addCase(loadMoreCourseTemplatesAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (state.adminCourseTemplatesData) {
          const newAdminCourseTemplatesData = {
            ...state.adminCourseTemplatesData,
            list: [
              ...(state.adminCourseTemplatesData.list || []),
              ...(action.payload.docs || []),
            ],
          };

          state.adminCourseTemplatesData = newAdminCourseTemplatesData;
        }
      })
      .addCase(getCourseTemplateByIdAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        state.adminCourseTemplate = result;
      })
      .addCase(adminUpdateCourseTemplate.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseTemplatesData) {
          const newAdminCourseTemplatesData = {
            ...state.adminCourseTemplatesData,
          };

          const newList = newAdminCourseTemplatesData.list || [];
          const index = newList.findIndex(
            (courseTemplate) => courseTemplate._id === result._id
          );

          if (index !== -1) {
            newList[index] = result;
          }

          newAdminCourseTemplatesData.list = newList;

          state.adminCourseTemplatesData = newAdminCourseTemplatesData;
        }
      })
      .addCase(adminDeleteCourseTemplate.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (state.adminCourseTemplatesData) {
          const newAdminCourseTemplatesData = {
            ...state.adminCourseTemplatesData,
          };

          const newList = (newAdminCourseTemplatesData.list || []).filter(
            (item) => item._id !== result._id
          );

          newAdminCourseTemplatesData.list = newList;
          state.adminCourseTemplatesData = newAdminCourseTemplatesData;
        }
      });

    builder.addMatcher(
      isPending(
        // Admin
        createCourseTemplateAsAdmin,
        getCourseTemplatesAsAdmin,
        loadMoreCourseTemplatesAsAdmin,
        getCourseTemplateByIdAsAdmin,
        adminUpdateCourseTemplate,
        adminDeleteCourseTemplate
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        // Admin
        createCourseTemplateAsAdmin,
        getCourseTemplatesAsAdmin,
        loadMoreCourseTemplatesAsAdmin,
        getCourseTemplateByIdAsAdmin,
        adminUpdateCourseTemplate,
        adminDeleteCourseTemplate
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const {
  resetAdminCourseTemplatesData,
  resetAdminCourseTemplate,
} = CourseTemplatesState.actions;

export const getCourseTemplatesState = (
  state: RootState
): RootState['courseTemplates'] => state.courseTemplates;

export * from './adminApi';
export default CourseTemplatesState.reducer;
